import React from "react";
import { IconButton, Tooltip, Typography, Fade, Box } from "@mui/material";
import { ContentCopy, Check } from "@mui/icons-material";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";

/**
 * Componente reutilizable para copiar texto al portapapeles
 * @param {string} text - Texto a copiar
 * @param {string} id - Identificador único para el elemento
 * @param {string} size - Tamaño del botón ('small', 'medium', 'large')
 * @param {string} successMessage - Mensaje a mostrar cuando se copia exitosamente
 * @param {boolean} showFeedback - Mostrar feedback visual debajo del botón
 * @param {number} timeout - Tiempo en ms para mostrar el feedback
 * @param {object} sx - Estilos adicionales de MUI
 * @param {function} onCopy - Callback opcional cuando se copia exitosamente
 * @param {function} onClick - Callback opcional para manejar el clic (útil para stopPropagation)
 */
const CopyButton = ({
  text,
  id,
  size = "small",
  successMessage = "¡Copiado!",
  showFeedback = true,
  timeout = 2000,
  sx = {},
  onCopy,
  onClick,
  ...otherProps
}) => {
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  const handleCopy = async (e) => {
    // Prevenir propagación del evento si se proporciona onClick
    if (onClick) {
      onClick(e);
    }

    // Prevenir propagación por defecto
    e.preventDefault();
    e.stopPropagation();

    const success = await copyToClipboard(text, id, timeout);
    if (success && onCopy) {
      onCopy(text);
    }
  };

  const copied = isCopied(id);

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Tooltip title={copied ? "¡Copiado!" : "Copiar"}>
        <IconButton
          size={size}
          onClick={handleCopy}
          sx={{
            transition: "all 0.2s ease",
            ...(copied && {
              color: "success.main",
            }),
            ...sx,
          }}
          {...otherProps}
        >
          {copied ? <Check fontSize={size} /> : <ContentCopy fontSize={size} />}
        </IconButton>
      </Tooltip>

      {showFeedback && copied && (
        <Fade in>
          <Typography
            variant="caption"
            color="success.main"
            sx={{ mt: 0.5, fontSize: "0.75rem" }}
          >
            {successMessage}
          </Typography>
        </Fade>
      )}
    </Box>
  );
};

export default CopyButton;
